import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Check, X, Navigation, Timer } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

export default function DriverOrderDetail() {
  const { order_no } = useParams();
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data: list } = await api.get("/driver/orders?include_done=true");
      const one = (list.items || []).find((o) => o.order_no === order_no);
      setOrder(one || null);
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order_no]);

  if (!order) return <p className="label-overline text-jow-navy/55">Loading…</p>;

  const isReservation = order.type === "reservation";

  const advance = async (status) => {
    setBusy(true);
    try {
      await api.post(`/driver/orders/${order.order_no}/status`, { status });
      toast.success(`Status: ${status.replace("_", " ")}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed.");
    } finally {
      setBusy(false);
    }
  };

  const decide = async (action) => {
    setBusy(true);
    try {
      await api.post(`/driver/orders/${order.order_no}/reservation-decision`, { action });
      toast.success(action === "accept" ? "Customer kept it." : "Returned — restocked.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed.");
    } finally {
      setBusy(false);
    }
  };

  const addr = order.address || {};
  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    `${addr.line1 || ""}, ${addr.city || ""}, ${addr.pincode || ""}`
  )}`;

  return (
    <div className="space-y-6" data-testid={`driver-order-${order.order_no}`}>
      <Link to="/driver/orders" className="label-overline text-jow-pink inline-flex items-center gap-1">
        <ChevronLeft size={13} /> Back
      </Link>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        <span className="label-overline text-jow-gold">
          {isReservation ? "Reservation" : "Order"}
        </span>
        <h2 className="font-display text-3xl text-jow-navy mt-1">{order.order_no}</h2>
        <p className="text-xs text-jow-navy/55 mt-1 flex items-center gap-2">
          Status: <strong>{order.status.replace("_", " ")}</strong>
          {isReservation && order.hold_expires_at && (
            <span className="inline-flex items-center gap-1 text-jow-pink">
              <Timer size={12} /> Hold until {new Date(order.hold_expires_at).toLocaleTimeString()}
            </span>
          )}
        </p>
      </motion.div>

      {/* Address card */}
      <div className="bg-white border border-jow-navy/10 p-4">
        <p className="label-overline text-jow-navy mb-2">Deliver to</p>
        <p className="text-sm text-jow-navy">
          <strong>{addr.name}</strong>
          <br />
          {addr.line1}
          {addr.line2 ? `, ${addr.line2}` : ""}
          <br />
          {addr.city} {addr.state} {addr.pincode}
          <br />
          <a href={`tel:${addr.phone}`} className="text-jow-pink">
            {addr.phone}
          </a>
        </p>
        <a
          href={mapsUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-flex items-center gap-2 label-overline text-jow-pink"
          data-testid="directions-link"
        >
          <Navigation size={12} /> Open in Maps
        </a>
      </div>

      {/* Items */}
      <div className="bg-white border border-jow-navy/10 p-4">
        <p className="label-overline text-jow-navy mb-3">Items</p>
        <ul className="divide-y divide-jow-navy/5">
          {order.items.map((i) => (
            <li key={i.sku} className="py-3 flex items-center gap-3">
              <img src={i.image} alt="" className="w-12 h-12 object-cover" />
              <div className="flex-1">
                <p className="text-sm text-jow-navy">{i.name}</p>
                <p className="text-[10px] text-jow-navy/55">
                  {i.sku} · qty {i.qty} · ₹{i.line_total.toLocaleString("en-IN")}
                </p>
              </div>
            </li>
          ))}
        </ul>
        <div className="border-t border-jow-navy/10 mt-3 pt-3 flex items-center justify-between text-sm">
          <span>Collect at door</span>
          <strong>₹{order.total.toLocaleString("en-IN")} ({order.payment.method.toUpperCase()})</strong>
        </div>
      </div>

      {/* Actions */}
      {isReservation && order.status === "reserved" && (
        <div className="grid grid-cols-2 gap-3" data-testid="reservation-actions">
          <button
            onClick={() => decide("accept")}
            disabled={busy}
            className="btn-primary-jow"
            data-testid="res-accept"
          >
            <Check size={14} /> Accept
          </button>
          <button
            onClick={() => decide("decline")}
            disabled={busy}
            className="btn-secondary-jow border-jow-pink text-jow-pink"
            data-testid="res-decline"
          >
            <X size={14} /> Decline
          </button>
        </div>
      )}

      {!isReservation && (
        <div className="flex flex-wrap gap-2" data-testid="status-actions">
          {order.status !== "on_rikshaw" && (
            <button onClick={() => advance("on_rikshaw")} disabled={busy} className="btn-secondary-jow text-[10px]" data-testid="adv-on-rikshaw">
              On rikshaw
            </button>
          )}
          {order.status !== "out_for_delivery" && order.status !== "delivered" && (
            <button onClick={() => advance("out_for_delivery")} disabled={busy} className="btn-secondary-jow text-[10px]" data-testid="adv-out">
              Start trip
            </button>
          )}
          {order.status !== "delivered" && (
            <button onClick={() => advance("delivered")} disabled={busy} className="btn-primary-jow text-[10px]" data-testid="adv-delivered">
              Mark delivered
            </button>
          )}
        </div>
      )}
    </div>
  );
}
